import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  GraduationCap,
  LineChart,
  Megaphone,
  Mic2,
  Rocket,
  ShoppingBag,
  Sparkles,
  Wrench,
} from 'lucide-react';

/** Contenu éditorial du centre d’aide LyrraOne. */
export interface HelpTopic {
  id: string;
  title: string;
  description: string;
}

export interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  topics: HelpTopic[];
}

export function findHelpTopicById(
  topicId: string,
  primaryCategories?: readonly HelpCategory[] | null,
): { category: HelpCategory; topic: HelpTopic } | null {
  const pools: HelpCategory[][] = [];
  if (primaryCategories?.length) pools.push([...primaryCategories]);
  pools.push(HELP_CENTER_CATEGORIES);
  for (const categories of pools) {
    for (const category of categories) {
      const topic = category.topics.find((t) => t.id === topicId);
      if (topic) return { category, topic };
    }
  }
  return null;
}

/** Catalogue statique LyrraOne — formateurs indépendants. */
export const HELP_CENTER_CATEGORIES: HelpCategory[] = [
  {
    id: 'demarrage',
    title: 'Démarrage & compte',
    description:
      'Prendre en main LyrraOne : tableau de bord, menu, profil et abonnement formateur.',
    icon: Rocket,
    topics: [
      {
        id: 'demarrage-tableau-bord',
        title: 'Tableau de bord formateur',
        description:
          'Vue d’ensemble de votre activité : formations, ventes récentes et raccourcis marketing.',
      },
      {
        id: 'demarrage-navigation',
        title: 'Menu & navigation',
        description:
          'Retrouver Création, Marketing, Pilotage et Médias dans le menu latéral LyrraOne.',
      },
      {
        id: 'demarrage-profil',
        title: 'Profil & paramètres',
        description:
          'Mettre à jour vos informations, la langue d’interface et les notifications.',
      },
      {
        id: 'lo-plans',
        title: 'Abonnement & limites du plan',
        description:
          'Comprendre les quotas LyrraOne (formations, contacts, envois) et passer à un plan supérieur.',
      },
    ],
  },
  {
    id: 'formations',
    title: 'Formations simples',
    description:
      'Créer, structurer et publier des formations en modules et chapitres sans designer graphique.',
    icon: BookOpen,
    topics: [
      {
        id: 'lo-formation-creer',
        title: 'Créer une formation',
        description:
          'Démarrer un nouveau parcours, choisir un titre et accéder à l’éditeur simple.',
      },
      {
        id: 'lo-formation-structure',
        title: 'Modules & chapitres',
        description:
          'Organiser le contenu en modules, réordonner les chapitres et gérer la progression linéaire.',
      },
      {
        id: 'lo-formation-contenu',
        title: 'Contenus pédagogiques',
        description:
          'Ajouter texte, vidéo, audio, quiz et documents dans chaque chapitre.',
      },
      {
        id: 'lo-formation-ia',
        title: 'Génération avec l’IA',
        description:
          'Accélérer la rédaction de modules ou de quiz grâce à l’assistant IA LyrraOne.',
      },
      {
        id: 'lo-formation-store',
        title: 'Publier sur le Store',
        description:
          'Rendre votre formation visible sur le Store LyrraOne et la proposer à la vente ou en accès libre.',
      },
      {
        id: 'lo-formation-gamification',
        title: 'Gamification apprenant',
        description:
          'XP, niveaux, bonus rapidité et classement par formation pour motiver la progression.',
      },
      {
        id: 'lo-formation-satisfaction',
        title: 'Enquête de satisfaction',
        description:
          'Collecter un avis NPS ou étoiles à la fin de la formation, configurable dans les paramètres du parcours.',
      },
      {
        id: 'lo-formation-sequential-lock',
        title: 'Verrouillage séquentiel',
        description:
          'Empêcher l’apprenant d’accéder aux blocs suivants tant que le précédent n’est pas terminé (player designer).',
      },
    ],
  },
  {
    id: 'store',
    title: 'Store LyrraOne',
    description:
      'Vendre vos formations, suivre les achats et gérer les apprenants du Store.',
    icon: ShoppingBag,
    topics: [
      {
        id: 'lo-store-vitrine',
        title: 'Vitrine & fiches produit',
        description:
          'Présenter vos formations : visuel, description, prix et visibilité sur le Store public.',
      },
      {
        id: 'lo-store-ventes',
        title: 'Ventes & apprenants',
        description:
          'Consulter les ventes, attribuer l’accès et suivre qui a acheté ou reçu une formation.',
      },
      {
        id: 'lo-store-prix',
        title: 'Tarifs & paiements',
        description:
          'Fixer un prix, proposer un accès gratuit et connecter votre compte de paiement.',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing & audience',
    description:
      'Développer votre audience : contacts, e-mails, SMS, automatisations et entonnoirs de vente.',
    icon: Megaphone,
    topics: [
      {
        id: 'lo-contacts',
        title: 'Contacts & tags',
        description:
          'Centraliser vos prospects et clients, segmenter avec des tags et importer des listes.',
      },
      {
        id: 'lo-email',
        title: 'Campagnes e-mail',
        description:
          'Créer des newsletters et séquences avec l’éditeur par blocs et les modèles LyrraOne.',
      },
      {
        id: 'lo-sms',
        title: 'Campagnes SMS',
        description:
          'Envoyer des SMS ciblés à vos contacts pour relancer ou annoncer une offre.',
      },
      {
        id: 'lo-automations',
        title: 'Automatisations',
        description:
          'Déclencher des actions (e-mail, tag, notification) selon le comportement de vos contacts.',
      },
      {
        id: 'lo-funnels',
        title: 'Entonnoirs de vente',
        description:
          'Construire des pages de capture et des parcours de conversion vers vos formations.',
      },
      {
        id: 'lo-funnel-order-bumps',
        title: 'Order bumps checkout',
        description:
          'Proposer jusqu’à 3 offres complémentaires (formations numériques ou produits physiques) sur la page de paiement du funnel.',
      },
      {
        id: 'lo-funnel-upsell',
        title: 'Upsell & downsell post-achat',
        description:
          'Afficher une offre complémentaire sur la page merci avec achat express Stripe (1 clic) si l’acheteur est connecté.',
      },
      {
        id: 'lo-site',
        title: 'Site web formateur',
        description:
          'Publier un mini-site vitrine : pages, sections, catalogue formations et blocs packs.',
      },
      {
        id: 'lo-formation-bundles',
        title: 'Packs formations',
        description:
          'Regrouper plusieurs formations à prix réduit et les afficher sur votre site ou store via le bloc « Packs ».',
      },
      {
        id: 'lo-creator-page',
        title: 'Page créateur & bio link',
        description:
          'Une page unique avec tous vos liens : formations, réseaux sociaux et prise de rendez-vous.',
      },
      {
        id: 'lo-crm',
        title: 'Pipeline CRM',
        description:
          'Suivre vos opportunités commerciales de la prise de contact à la vente.',
      },
      {
        id: 'lo-affiliate',
        title: 'Programme d’affiliation',
        description:
          'Permettre à des partenaires de promouvoir vos formations contre une commission.',
      },
    ],
  },
  {
    id: 'engagement',
    title: 'Engagement & communauté',
    description:
      'Renforcer la relation avec votre audience : RDV, communautés, webinaires et produits physiques.',
    icon: Sparkles,
    topics: [
      {
        id: 'lo-booking',
        title: 'Prise de rendez-vous',
        description:
          'Proposer des créneaux de coaching ou d’appel découverte directement en ligne.',
      },
      {
        id: 'lo-communities',
        title: 'Communautés',
        description:
          'Créer un espace d’échange pour vos apprenants ou votre audience engagée.',
      },
      {
        id: 'lo-webinars',
        title: 'Webinaires evergreen',
        description:
          'Diffuser un webinaire enregistré en continu pour générer des leads.',
      },
      {
        id: 'lo-physical-products',
        title: 'Produits physiques',
        description:
          'Vendre des livres, supports ou goodies en complément de vos formations.',
      },
    ],
  },
  {
    id: 'apprenants',
    title: 'Espace apprenant',
    description:
      'Ce que voient vos apprenants : parcours, progression et temps d’apprentissage.',
    icon: GraduationCap,
    topics: [
      {
        id: 'lo-learner-parcours',
        title: 'Parcours apprenant',
        description:
          'L’expérience côté apprenant : accès aux formations, reprise de lecture et navigation chapitres.',
      },
      {
        id: 'lo-learner-progression',
        title: 'Progression & temps passé',
        description:
          'Suivre l’avancement (% de complétion), le temps d’apprentissage et l’activité récente.',
      },
    ],
  },
  {
    id: 'medias',
    title: 'Médias & podcasts',
    description:
      'Bibliothèque multimédia et création de podcasts pour enrichir votre offre.',
    icon: Mic2,
    topics: [
      {
        id: 'medias-bibliotheque',
        title: 'Bibliothèque média',
        description:
          'Importer, organiser et réutiliser vidéos, sons et images dans vos formations.',
      },
      {
        id: 'lo-podcasts',
        title: 'Podcasts & livres audio',
        description:
          'Créer des épisodes audio, métadonnées et couverture pour compléter vos formations.',
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Pilotage & analytiques',
    description:
      'Mesurer l’impact de vos formations et de vos actions marketing.',
    icon: LineChart,
    topics: [
      {
        id: 'lo-analytics-dashboard',
        title: 'Tableau de bord analytique',
        description:
          'Indicateurs clés : ventes, inscriptions, engagement et évolution dans le temps.',
      },
    ],
  },
  {
    id: 'support',
    title: 'Dépannage & support',
    description:
      'Résoudre les problèmes courants et contacter l’équipe LyrraOne.',
    icon: Wrench,
    topics: [
      {
        id: 'support-faq',
        title: 'Problèmes fréquents',
        description:
          'Lecteur qui ne charge pas, e-mail non reçu, paiement en attente : premiers réflexes.',
      },
      {
        id: 'support-contact',
        title: 'Contacter le support Lyrra',
        description:
          'Canal support, création de ticket et informations à fournir pour un diagnostic rapide.',
      },
    ],
  },
];
